import React from 'react';
import styled, { createGlobalStyle, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

export type Status =
  | 'merged'
  | 'fixed'
  | 'submitted'
  | 'active'
  | 'published'
  | 'live'
  | 'shipped';

export interface ProjectLink {
  label: string;
  url: string;
}

export interface Project {
  title: string;
  target?: string;
  description: string;
  impact?: string;
  status?: Status;
  statusLabel?: string;
  tags?: string[];
  links?: ProjectLink[];
  icon?: IconDefinition;
  iconColor?: string;
  iconBg?: string;
}

interface GlobalProps {
  $accent?: string;
}

function hexToRgba(hex: string, alpha: number): string {
  const m = hex.replace('#', '');
  const full = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const DEFAULT_ACCENT = '#22d3ee';

export const MinimalGlobalStyle = createGlobalStyle<GlobalProps>`
  :root {
    --accent: ${p => p.$accent ?? DEFAULT_ACCENT};
    --accent-08: ${p => hexToRgba(p.$accent ?? DEFAULT_ACCENT, 0.08)};
    --accent-12: ${p => hexToRgba(p.$accent ?? DEFAULT_ACCENT, 0.12)};
    --accent-18: ${p => hexToRgba(p.$accent ?? DEFAULT_ACCENT, 0.18)};
    --accent-25: ${p => hexToRgba(p.$accent ?? DEFAULT_ACCENT, 0.25)};
    --accent-35: ${p => hexToRgba(p.$accent ?? DEFAULT_ACCENT, 0.35)};
    --accent-50: ${p => hexToRgba(p.$accent ?? DEFAULT_ACCENT, 0.5)};
  }

  * {
    scrollbar-width: thin;
    scrollbar-color: var(--accent-25) #07080b;
  }

  *::-webkit-scrollbar {
    width: 8px;
    background-color: #07080b;
  }

  *::-webkit-scrollbar-thumb {
    background: var(--accent-25);
    border-radius: 4px;

    &:hover {
      background: var(--accent-50);
    }
  }

  body {
    margin: 0;
    padding: 0;
    background: #07080b;
    color: #e8e8ec;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
      Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

export const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow-y: auto;
  overflow-x: hidden;
  background:
    radial-gradient(
      ellipse 90% 60% at 50% -10%,
      var(--accent-18) 0%,
      transparent 55%
    ),
    linear-gradient(180deg, #0b0d12 0%, #07080b 60%);

  &::before {
    content: '';
    position: fixed;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
    background-size: 48px 48px;
    background-position: center center;
    pointer-events: none;
    mask-image: radial-gradient(ellipse at center, black 25%, transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse at center, black 25%, transparent 75%);
    z-index: 0;
  }

  &::after {
    content: '';
    position: fixed;
    inset: 0;
    background: radial-gradient(
      ellipse at center,
      transparent 50%,
      rgba(0, 0, 0, 0.5) 100%
    );
    pointer-events: none;
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

export const Inner = styled.div`
  max-width: 720px;
  margin: 0 auto;
  padding: 5rem 2rem 6rem;

  @media (max-width: 768px) {
    padding: 4rem 1.25rem 4rem;
  }
`;

const BackButtonBase = styled.button`
  position: fixed;
  top: 1.5rem;
  left: 1.5rem;
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: color 0.2s ease, text-shadow 0.2s ease;
  z-index: 100;
  padding: 0.5rem;

  &:hover {
    color: var(--accent);
    text-shadow: 0 0 12px var(--accent-50);
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
  }
`;

export const BackButton: React.FC<{ to?: string; label?: string }> = ({
  to,
  label = '\u2190 Back',
}) => {
  const navigate = useNavigate();
  const handleClick = () => {
    if (to) {
      navigate(to);
      return;
    }
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  return <BackButtonBase onClick={handleClick}>{label}</BackButtonBase>;
};

export const HeroRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  margin-bottom: 1.25rem;
`;

export const Avatar = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 18px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid var(--accent-25);
  box-shadow: 0 0 32px var(--accent-18);
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--accent-18) 0%, transparent 60%);
    pointer-events: none;
  }
`;

export const HeroText = styled.div`
  min-width: 0;
  flex: 1;
`;

export const IconTile = styled.div<{ $color?: string; $bg?: string }>`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: ${p => p.$bg ?? 'var(--accent-12)'};
  color: ${p => p.$color ?? 'var(--accent)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1.05rem;
  box-shadow:
    inset 0 0 0 1px ${p => (p.$color ? `${p.$color}22` : 'var(--accent-18)')},
    0 0 20px ${p => (p.$color ? `${p.$color}22` : 'var(--accent-08)')};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const Header = styled.header`
  margin-bottom: 3rem;
`;

export const PageTitle = styled.h1`
  font-size: 2.25rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
  text-shadow: 0 0 40px var(--accent-35);

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

export const Bio = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 0 1.25rem;
  line-height: 1.6;
`;

export const HeaderLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
`;

export const HeaderLink = styled.a`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  transition: color 0.2s ease, text-shadow 0.2s ease;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;

  &:hover {
    color: var(--accent);
    text-shadow: 0 0 12px var(--accent-50);
  }
`;

export const Section = styled.section`
  margin-bottom: 3.5rem;
`;

export const SectionTitle = styled.h2`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--accent);
  text-transform: uppercase;
  letter-spacing: 0.14em;
  margin: 0 0 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  opacity: 0.85;

  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(
      90deg,
      var(--accent-25) 0%,
      transparent 100%
    );
  }
`;

export const Card = styled.div`
  padding: 1.25rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: background 0.2s ease, border-color 0.2s ease;

  &:first-of-type {
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }

  &:hover {
    border-bottom-color: var(--accent-25);
  }
`;

export const CardTop = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.375rem;
  flex-wrap: wrap;
`;

export const CardTarget = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--accent);
  opacity: 0.7;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  display: block;
  margin-bottom: 0.25rem;
`;

export const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const badgeStyles: Record<Status, ReturnType<typeof css>> = {
  merged: css`color: #34d399; background: rgba(16, 185, 129, 0.15); box-shadow: 0 0 16px rgba(16, 185, 129, 0.1);`,
  fixed: css`color: #34d399; background: rgba(16, 185, 129, 0.15); box-shadow: 0 0 16px rgba(16, 185, 129, 0.1);`,
  live: css`color: #34d399; background: rgba(16, 185, 129, 0.15); box-shadow: 0 0 16px rgba(16, 185, 129, 0.1);`,
  shipped: css`color: #34d399; background: rgba(16, 185, 129, 0.15); box-shadow: 0 0 16px rgba(16, 185, 129, 0.1);`,
  submitted: css`color: #fbbf24; background: rgba(251, 191, 36, 0.15); box-shadow: 0 0 16px rgba(251, 191, 36, 0.1);`,
  active: css`color: #22d3ee; background: rgba(34, 211, 238, 0.15); box-shadow: 0 0 16px rgba(34, 211, 238, 0.1);`,
  published: css`color: #60a5fa; background: rgba(59, 130, 246, 0.15); box-shadow: 0 0 16px rgba(59, 130, 246, 0.1);`,
};

export const Badge = styled.span<{ $variant: Status }>`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  padding: 0.2rem 0.5rem;
  border-radius: 3px;
  white-space: nowrap;
  ${p => badgeStyles[p.$variant]}
`;

export const CardDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.72);
  line-height: 1.65;
  margin: 0 0 0.75rem;
`;

export const CardImpact = styled.p`
  font-family: 'SF Mono', 'Fira Code', 'Fira Mono', 'Roboto Mono', monospace;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 0 0 0.75rem;
  line-height: 1.5;

  &::before {
    content: '> ';
    color: var(--accent);
    opacity: 0.7;
  }
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 0.75rem;
`;

export const Tag = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.55);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 3px;
  padding: 0.15rem 0.5rem;
`;

export const CardLinks = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

export const CardLink = styled.a`
  font-size: 0.8125rem;
  color: var(--accent);
  opacity: 0.8;
  text-decoration: none;
  transition: opacity 0.2s ease, text-shadow 0.2s ease;

  &:hover {
    opacity: 1;
    text-shadow: 0 0 12px var(--accent-50);
  }
`;

export const InternalLink = styled.button`
  font-size: 0.8125rem;
  font-family: inherit;
  color: var(--accent);
  opacity: 0.8;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s ease, text-shadow 0.2s ease;

  &:hover {
    opacity: 1;
    text-shadow: 0 0 12px var(--accent-50);
  }
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1px;
  background: var(--accent-18);
  border: 1px solid var(--accent-25);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 3.5rem;
  box-shadow: 0 0 48px var(--accent-08);

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

export const StatCell = styled.div`
  background: #0a0b0f;
  padding: 1.5rem;
  text-align: center;
`;

export const StatValue = styled.div`
  font-size: 1.875rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.25rem;
  text-shadow: 0 0 24px var(--accent-50);
  letter-spacing: -0.01em;
`;

export const StatLabel = styled.div`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.55);
`;

export const Paragraph = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.62);
  line-height: 1.7;
  margin: 0 0 1rem;
`;

export const FindingsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 0.75rem;
`;

export const Finding = styled.li`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.6;
  padding: 0.2rem 0 0.2rem 1.25rem;
  position: relative;

  &::before {
    content: '>';
    position: absolute;
    left: 0;
    font-family: 'SF Mono', 'Fira Code', monospace;
    color: var(--accent);
    opacity: 0.7;
  }
`;

interface ProjectCardProps {
  project: Project;
  onLinkClick?: (project: string, label: string) => void;
}

const CardBody = styled.div`
  min-width: 0;
  flex: 1;
`;

const CardInner = styled.div`
  display: flex;
  gap: 1rem;
  align-items: flex-start;
`;

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onLinkClick }) => {
  const body = (
    <CardBody>
      <CardTop>
        <CardTitle>{project.title}</CardTitle>
        {project.status && project.statusLabel && (
          <Badge $variant={project.status}>{project.statusLabel}</Badge>
        )}
      </CardTop>
      <CardDescription>{project.description}</CardDescription>
      {project.impact && <CardImpact>{project.impact}</CardImpact>}
      {project.tags && project.tags.length > 0 && (
        <TagRow>
          {project.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
        </TagRow>
      )}
      {project.links && project.links.length > 0 && (
        <CardLinks>
          {project.links.map(link => (
            <CardLink
              key={link.label}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onLinkClick?.(project.title, link.label)}
            >
              {link.label} &rarr;
            </CardLink>
          ))}
        </CardLinks>
      )}
    </CardBody>
  );

  return (
    <Card>
      {project.target && <CardTarget>{project.target}</CardTarget>}
      {project.icon ? (
        <CardInner>
          <IconTile $color={project.iconColor} $bg={project.iconBg}>
            <FontAwesomeIcon icon={project.icon} />
          </IconTile>
          {body}
        </CardInner>
      ) : (
        body
      )}
    </Card>
  );
};
